import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { MeuTermoDetalhe, MeuTermoResumo } from './contrato';

/**
 * Os Termos de Adesão do credenciado, da API real (`bff/v1/termo-adesao`,
 * frente F11). O servidor decide de quem é o termo pelo token (vínculo em
 * `credenciado_operadores`); a tela nunca manda o cadastro.
 */
export const CAMINHO_DA_API = '/bff/v1/termo-adesao/meus-termos';

export const CHAVES_DE_CONTRATOS = {
  lista: ['credenciado', 'termos', 'lista'] as const,
  detalhe: (id: string) => ['credenciado', 'termos', 'detalhe', id] as const,
};

export function useMeusTermos() {
  return useQuery({
    queryKey: CHAVES_DE_CONTRATOS.lista,
    queryFn: () => chamarApi<MeuTermoResumo[]>(CAMINHO_DA_API),
    retry: naoInsistirEmRecusa,
  });
}

export function useMeuTermo(id: string) {
  return useQuery({
    queryKey: CHAVES_DE_CONTRATOS.detalhe(id),
    queryFn: () => chamarApi<MeuTermoDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(id)}`),
    retry: (tentativas, erro) =>
      !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro),
  });
}

/** Assinar (encaminha ao provedor) ou atualizar a situação da assinatura. */
export function useMinhaAssinatura(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (acao: 'assinar' | 'atualizar-assinatura') =>
      chamarApi<MeuTermoDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(id)}/${acao}`, {
        metodo: 'POST',
        corpo: {},
      }),
    onSuccess: (termo) => {
      cliente.setQueryData(CHAVES_DE_CONTRATOS.detalhe(id), termo);
      void cliente.invalidateQueries({ queryKey: CHAVES_DE_CONTRATOS.lista });
    },
    onError: () => void cliente.invalidateQueries({ queryKey: CHAVES_DE_CONTRATOS.detalhe(id) }),
  });
}

export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}
